import { useState, useEffect, useCallback, useRef } from 'react';
import { LIST_PAGE_SIZE } from '../constants';

/**
 * Shared pagination + fetch + archive/unarchive logic for list pages.
 *
 * @param {object} opts
 * @param {Function} opts.fetchFn       - (cursor, filters) => Promise<{ items, total, next_cursor }>
 * @param {Function} [opts.archiveFn]   - (id) => Promise  — optional
 * @param {Function} [opts.unarchiveFn] - (id) => Promise  — optional
 * @param {object}   opts.filters       - plain object; value changes trigger refetch + reset to page 1
 *
 * @returns {{
 *   items: any[],
 *   total: number,
 *   loading: boolean,
 *   fetchError: string,
 *   selectedIds: any[],
 *   setSelectedIds: Function,
 *   page: number,
 *   totalPages: number,
 *   handleNext: Function,
 *   handlePrev: Function,
 *   handleArchive: Function | undefined,
 *   handleUnarchive: Function | undefined,
 * }}
 */
export default function useEntityList({ fetchFn, archiveFn, unarchiveFn, filters }) {
  // Keep a ref so fetchPage can always read the latest filters without
  // needing them in its own dependency array (avoids stale closure).
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  // cursorHistory[i] is the cursor needed to reach page i+1.
  // Page 1 uses null; later pages use the next_cursor returned by the
  // previous page.
  const [cursorHistory, setCursorHistory] = useState([null]);
  const [pageIndex, setPageIndex] = useState(0); // 0-based

  const fetchPage = useCallback(async (cursor) => {
    setLoading(true);
    setFetchError('');
    setSelectedIds([]);
    try {
      const data = await fetchFn(cursor, filtersRef.current);
      setItems(data.items || []);
      setTotal(data.total ?? 0);
      if (data.next_cursor) {
        setCursorHistory((prev) => {
          // Use prev.indexOf (not captured cursorHistory) to avoid stale closure.
          const nextIdx = prev.indexOf(cursor) + 1;
          if (nextIdx >= prev.length) return [...prev, data.next_cursor];
          return prev;
        });
      }
    } catch (err) {
      if (err) setFetchError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchFn]);

  // Stringify filters so the effect dep is stable across re-renders when
  // the object reference changes but the values haven't.
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    setCursorHistory([null]);
    setPageIndex(0);
    fetchPage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const handleNext = useCallback(() => {
    const nextIndex = pageIndex + 1;
    const cursor = cursorHistory[nextIndex] ?? cursorHistory[cursorHistory.length - 1];
    setPageIndex(nextIndex);
    fetchPage(cursor);
  }, [pageIndex, cursorHistory, fetchPage]);

  const handlePrev = useCallback(() => {
    const prevIndex = pageIndex - 1;
    setPageIndex(prevIndex);
    fetchPage(cursorHistory[prevIndex]);
  }, [pageIndex, cursorHistory, fetchPage]);

  const handleArchive = useCallback(async (ids) => {
    if (!archiveFn || !ids || ids.length === 0) return;
    for (const id of ids) {
      try {
        await archiveFn(id);
      } catch (err) {
        console.error(`Failed to archive ${id}:`, err);
      }
    }
    setCursorHistory([null]);
    setPageIndex(0);
    fetchPage(null);
  }, [archiveFn, fetchPage]);

  const handleUnarchive = useCallback(async (ids) => {
    if (!unarchiveFn || !ids || ids.length === 0) return;
    for (const id of ids) {
      try {
        await unarchiveFn(id);
      } catch (err) {
        console.error(`Failed to restore ${id}:`, err);
      }
    }
    setCursorHistory([null]);
    setPageIndex(0);
    fetchPage(null);
  }, [unarchiveFn, fetchPage]);

  return {
    items,
    total,
    loading,
    fetchError,
    selectedIds,
    setSelectedIds,
    page: pageIndex + 1,
    totalPages: Math.ceil(total / LIST_PAGE_SIZE) || 1,
    handleNext,
    handlePrev,
    handleArchive: archiveFn ? handleArchive : undefined,
    handleUnarchive: unarchiveFn ? handleUnarchive : undefined,
  };
}
